<template>
  <v-form ref="form" v-model="valid" lazy-validation class="pa-4">
    <v-row dense>
      <!-- انتخاب مارکت‌پلیس -->
      <v-col cols="12" md="6">
        <v-select
          v-model="marketplace"
          :items="marketplaces"
          label="مارکت‌پلیس مقصد"
          :disabled="editMode"
          outlined
          dense
          @change="onMarketplaceChange"
        />
      </v-col>

      <!-- انتخاب محصول داخلی سیستم -->
      <v-col cols="12" md="6">
        <v-autocomplete
          v-model="form.product_id"
          :items="products"
          item-text="title"
          item-value="id"
          label="انتخاب محصول سیستم"
          :rules="[rules.required]"
          :disabled="editMode"
          outlined
          dense
          @change="onProductChange"
        />
      </v-col>
    </v-row>

    <!-- بخش اختصاصی اتصال دیجی‌کالا (حالت ایجاد جدید) -->
    <template v-if="!editMode && marketplace === 'digikala'">
      <v-divider class="my-3" />
      <div class="subtitle-2 font-weight-bold mb-3 d-flex align-center">
        <v-icon small color="red" class="ml-1">mdi-shopping</v-icon>
        اطلاعات محصول دیجی‌کالا
      </div>

      <v-row dense>
        <!-- انتخاب محصول دیجی‌کالا به صورت Autocomplete با جستجو -->
        <v-col cols="12" md="6">
          <v-autocomplete
            ref="digiProductSelect"
            v-model="form.digikala_product_id"
            :items="digikalaProducts"
            :loading="isLoadingProducts"
            :search-input.sync="searchDigiProduct"
            item-value="id"
            item-text="title"
            label="محصول دیجی‌کالا (جستجو با نام یا کد DKP)"
            placeholder="نام یا کد کالا را تایپ کنید..."
            :rules="[rules.required]"
            outlined
            dense
            clearable
            no-filter
            prepend-inner-icon="mdi-magnify"
            @change="handleDigiProductChange"
          >
            <template v-slot:selection="{ item }">
              <span class="text-truncate">
                <v-chip x-small color="primary" class="ml-1">DKP-{{ item.id }}</v-chip>
                {{ item.title }}
              </span>
            </template>
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title class="text-body-2">{{ item.title }}</v-list-item-title>
                <v-list-item-subtitle class="text-caption text-primary">کد: {{ item.id }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12" md="6" class="d-flex align-center">
          <v-checkbox
            v-model="hasVariants"
            label="محصول دارای تنوع (Variant) است"
            hide-details
            dense
            :disabled="!skus.length"
          />
        </v-col>
      </v-row>

      <!-- نگاشت تنوع‌ها با لیست دراپ‌داون تنوع‌های دیجی‌کالا -->
      <div v-if="hasVariants && skus.length" class="mt-3">
        <div class="caption grey--text mb-2">
          تنوع متناظر در دیجی‌کالا را برای هر تنوع داخلی انتخاب کنید:
        </div>

        <v-card outlined class="pa-3 rounded-lg grey lighten-5">
          <v-row
            v-for="(sku, index) in skus"
            :key="sku.id"
            align="center"
            dense
            class="mb-2"
          >
            <v-col cols="12" md="5">
              <div class="font-weight-medium text-body-2">
                {{ sku.title || ('تنوع ' + (index + 1)) }}
                <span class="grey--text caption">({{ sku.sku }})</span>
              </div>
            </v-col>
            <v-col cols="12" md="7">
              <v-autocomplete
                ref="skuSelect"
                v-model="variantMappings[sku.id]"
                :items="digikalaVariants"
                :loading="isLoadingVariants"
                item-value="id"
                item-text="title"
                label="انتخاب تنوع دیجی‌کالا (DKPC)"
                placeholder="انتخاب تنوع..."
                outlined
                dense
                hide-details
                clearable
                class="white"
              >
                <template v-slot:selection="{ item }">
                  <span class="text-truncate">
                    <v-chip x-small color="secondary" class="ml-1">DKPC-{{ item.id }}</v-chip>
                    {{ item.title }}
                  </span>
                </template>
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">{{ item.title }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption">کد تنوع: {{ item.id }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
        </v-card>
      </div>
    </template>

    <!-- بخش حالت ویرایش یا سایر مارکت‌پلیس‌ها -->
    <template v-else>
      <v-divider class="my-3" />
      <v-row dense>
        <!-- انتخاب تنوع محصول سیستم -->
        <v-col v-if="skus.length" cols="12" md="6">
          <v-select
            v-model="form.product_sku_id"
            :items="skus"
            item-text="title"
            item-value="id"
            label="تنوع محصول سیستم"
            clearable
            outlined
            dense
            :disabled="editMode"
          />
        </v-col>

        <!-- شناسه محصول در مارکت -->
        <v-col cols="12" :md="skus.length ? 6 : 12">
          <template v-if="marketplace === 'digikala'">
            <v-autocomplete
              ref="editDigiProductSelect"
              v-model="form.external_id"
              :items="digikalaProducts"
              :loading="isLoadingProducts"
              :search-input.sync="searchDigiProduct"
              item-value="id"
              item-text="title"
              label="شناسه کالا در دیجی‌کالا (DKP)"
              placeholder="جستجو با نام یا شناسه DKP..."
              :rules="[rules.required]"
              outlined
              dense
              clearable
              no-filter
              prepend-inner-icon="mdi-magnify"
              @change="handleDigiProductChange"
            >
              <template v-slot:selection="{ item }">
                <span class="text-truncate">
                  <v-chip x-small color="primary" class="ml-1">DKP-{{ item.id }}</v-chip>
                  {{ item.title }}
                </span>
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">{{ item.title }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption text-primary">شناسه: {{ item.id }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </template>
          <template v-else>
            <v-text-field
              v-model="form.external_id"
              label="شناسه محصول در مارکت (external_id)"
              :rules="[rules.required]"
              outlined
              dense
              class="ltr-input"
            />
          </template>
        </v-col>

        <!-- شناسه تنوع در مارکت -->
        <v-col cols="12" md="6">
          <template v-if="marketplace === 'digikala'">
            <v-autocomplete
              ref="editVariantSelect"
              v-model="form.external_variant_id"
              :items="digikalaVariants"
              :loading="isLoadingVariants"
              item-value="id"
              item-text="title"
              label="شناسه تنوع در دیجی‌کالا (DKPC)"
              placeholder="انتخاب تنوع..."
              clearable
              outlined
              dense
            >
              <template v-slot:selection="{ item }">
                <span class="text-truncate">
                  <v-chip x-small color="secondary" class="ml-1">DKPC-{{ item.id }}</v-chip>
                  {{ item.title }}
                </span>
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">{{ item.title }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption">کد تنوع: {{ item.id }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </template>
          <template v-else>
            <v-text-field
              v-model="form.external_variant_id"
              label="شناسه تنوع در مارکت (external_variant_id)"
              outlined
              dense
              class="ltr-input"
            />
          </template>
        </v-col>

        <!-- وضعیت انتشار/فعال -->
        <v-col cols="12" md="6">
          <v-switch
            v-model="form.active"
            label="اتصال فعال باشد"
            color="success"
            dense
            inset
          />
        </v-col>
      </v-row>
    </template>

    <!-- دکمه‌ها -->
    <div class="d-flex justify-end mt-4">
      <v-btn class="btn-delete rounded-lg ml-2" @click="$emit('close')">
        <v-icon left>mdi-close</v-icon>انصراف
      </v-btn>
      <v-spacer />
      <v-btn
        class="btn-get-code rounded-lg px-6"
        :loading="saving"
        @click="save"
      >
        <v-icon left>mdi-content-save</v-icon>
        {{ editMode ? 'ویرایش اطلاعات' : 'ایجاد اتصال' }}
      </v-btn>
    </div>
  </v-form>
</template>

<script>
export default {
  name: "ListingFormModal",
  props: {
    listing: { type: Object, default: null },
    editMode: { type: Boolean, default: false },
  },
  data() {
    return {
      valid: true,
      saving: false,
      marketplace: "digikala",
      hasVariants: false,
      products: [],
      skus: [],
      variantMappings: {},

      digikalaProducts: [],
      isLoadingProducts: false,
      searchDigiProduct: null,
      searchTimeout: null,
      digikalaVariants: [],
      isLoadingVariants: false,

      marketplaces: [
        { text: "دیجی‌کالا", value: "digikala" },
        { text: "باسلام", value: "basalam" },
        { text: "اسنپ‌شاپ", value: "snappshop" },
      ],

      form: {
        product_id: null,
        product_sku_id: null,
        digikala_product_id: null,
        external_id: "",
        external_variant_id: "",
        active: true,
      },

      rules: {
        required: (v) => (v !== null && v !== "" && v !== undefined) || "این فیلد الزامی است.",
      },
    };
  },
  watch: {
    searchDigiProduct(val) {
      if (!val || val.length < 2) return;
      if (this.isLoadingProducts) return;

      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchDigikalaProducts(val);
      }, 500);
    },
  },
  async mounted() {
    this.fetchProductOptions();
    await this.fetchDigikalaProducts("");

    if (this.editMode && this.listing) {
      this.marketplace = this.listing.marketplace || "digikala";

      const extId = this.listing.external_id ? Number(this.listing.external_id) : "";
      const extVarId = this.listing.external_variant_id ? Number(this.listing.external_variant_id) : "";

      this.form = {
        product_id: this.listing.product_id,
        product_sku_id: this.listing.product_sku_id,
        external_id: extId,
        external_variant_id: extVarId,
        active: !!this.listing.active,
      };

      if (this.listing.product_id) {
        await this.fetchSkus(this.listing.product_id);
      }

      if (this.marketplace === "digikala") {
        if (extId) {
          // ۱. در صورتی که محصول دیجی‌کالا در لیست دریافتی نبود، دستی تزریق شود
          if (!this.digikalaProducts.some((p) => Number(p.id) === extId)) {
            this.digikalaProducts.unshift({
              id: extId,
              title: this.listing?.metadata?.title || `محصول ${extId}`,
            });
          }

          // ۲. فراخوانی لیست تنوع‌ها از سرور
          await this.fetchDigikalaVariants(extId);

          // ۳. کلید حل مشکل: اگر تنوع متصل در لیست دریافت‌شده نبود، آن را تزریق کن تا خالی نمایش داده نشود
          if (extVarId && !this.digikalaVariants.some((v) => Number(v.id) === extVarId)) {
            const variantTitle =
              this.listing?.metadata?.variant_title ||
              this.listing?.metadata?.color ||
              this.listing?.metadata?.title ||
              `تنوع ${extVarId}`;

            this.digikalaVariants.unshift({
              id: extVarId,
              title: variantTitle,
            });
          }

          // اگر تنوع مقدار نداشت منوی کشویی تنوع باز شود
          if (!extVarId) {
            this.openEditVariantDropdownIfEmpty();
          }
        } else {
          this.openEditProductDropdownIfEmpty();
        }
      }
    } else {
      // در حالت ساخت جدید اگر محصول دیجی‌کالا خالی است باز شود
      if (this.marketplace === "digikala") {
        this.openDigiProductDropdownIfEmpty();
      }
    }
  },
  methods: {
    openDigiProductDropdownIfEmpty() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (!this.form.digikala_product_id && this.$refs.digiProductSelect) {
            this.$refs.digiProductSelect.focus();
            this.$refs.digiProductSelect.isMenuActive = true;
          }
        }, 200);
      });
    },

    openEditProductDropdownIfEmpty() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (!this.form.external_id && this.$refs.editDigiProductSelect) {
            this.$refs.editDigiProductSelect.focus();
            this.$refs.editDigiProductSelect.isMenuActive = true;
          }
        }, 200);
      });
    },

    openEditVariantDropdownIfEmpty() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (!this.form.external_variant_id && this.$refs.editVariantSelect) {
            this.$refs.editVariantSelect.focus();
            this.$refs.editVariantSelect.isMenuActive = true;
          }
        }, 200);
      });
    },

    openFirstEmptySkuDropdown() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (!this.$refs.skuSelect) return;
          const skuRefs = Array.isArray(this.$refs.skuSelect) ? this.$refs.skuSelect : [this.$refs.skuSelect];
          for (let i = 0; i < this.skus.length; i++) {
            const sku = this.skus[i];
            if (!this.variantMappings[sku.id] && skuRefs[i]) {
              skuRefs[i].focus();
              skuRefs[i].isMenuActive = true;
              break;
            }
          }
        }, 250);
      });
    },

    async fetchProductOptions() {
      try {
        const { data } = await this.$api.get("/v1/products?length=100");
        this.products = data.data?.data || data.data || [];
      } catch (e) {
        this.$toast.error("خطا در دریافت لیست محصولات");
      }
    },

    async fetchSkus(productId) {
      try {
        const { data } = await this.$api.get(`/v1/products/${productId}/skus`);
        this.skus = data.data?.data || data.data || [];
        this.hasVariants = this.skus.length > 0;
      } catch (e) {
        this.skus = [];
        this.hasVariants = false;
      }
    },

    onProductChange(val) {
      this.form.product_sku_id = null;
      this.variantMappings = {};
      if (val) {
        this.fetchSkus(val);
      } else {
        this.skus = [];
        this.hasVariants = false;
      }
    },

    onMarketplaceChange(val) {
      if (val === "digikala") {
        if (!this.digikalaProducts.length) {
          this.fetchDigikalaProducts("");
        }
        this.openDigiProductDropdownIfEmpty();
      }
    },

    async fetchDigikalaProducts(searchQuery) {
      this.isLoadingProducts = true;
      try {
        const { data } = await this.$api.get(`/v1/digikala/products/list`, {
          params: { search: searchQuery },
        });
        this.digikalaProducts = data?.data?.data || data?.data || data || [];
      } catch (error) {
        console.error("خطا در دریافت محصولات دیجی‌کالا", error);
      } finally {
        this.isLoadingProducts = false;
      }
    },

    async fetchDigikalaVariants(productId) {
      if (!productId) {
        this.digikalaVariants = [];
        return;
      }
      this.isLoadingVariants = true;
      try {
        const { data } = await this.$api.get(`/v1/digikala/variants/list`, {
          params: { product_id: productId },
        });
        this.digikalaVariants = data?.data?.data || data?.data || data || [];
      } catch (error) {
        this.$toast.error("خطا در دریافت تنوع‌های دیجی‌کالا");
      } finally {
        this.isLoadingVariants = false;
      }
    },

    async handleDigiProductChange(productId) {
      this.variantMappings = {};
      this.form.external_variant_id = "";
      if (productId) {
        await this.fetchDigikalaVariants(productId);
        if (this.editMode) {
          this.openEditVariantDropdownIfEmpty();
        } else if (this.hasVariants && this.skus.length) {
          this.openFirstEmptySkuDropdown();
        }
      } else {
        this.digikalaVariants = [];
      }
    },

    async save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;

      try {
        if (this.editMode) {
          const payload = {
            external_id: String(this.form.external_id),
            external_variant_id: this.form.external_variant_id ? String(this.form.external_variant_id) : null,
            active: this.form.active,
          };
          await this.$api.post(`/v1/marketplace-listings/${this.listing.id}`, payload);
          this.$toast.success("لیستینگ با موفقیت ویرایش شد.");
        } else if (this.marketplace === "digikala") {
          let payload = {
            digikala_product_id: Number(this.form.digikala_product_id),
            product_id: Number(this.form.product_id),
          };

          if (this.hasVariants && this.skus.length) {
            const mappings = Object.keys(this.variantMappings)
              .filter((skuId) => !!this.variantMappings[skuId])
              .map((skuId) => ({
                digikala_variant_id: Number(this.variantMappings[skuId]),
                product_sku_id: Number(skuId),
              }));

            if (mappings.length === 0) {
              this.$toast.warning("لطفاً حداقل یک تنوع دیجی‌کالا را متصل کنید.");
              this.saving = false;
              return;
            }
            payload.mappings = mappings;
          }

          await this.$api.post("/v1/marketplace-listings/digi/link-product", payload);
          this.$toast.success("محصول با موفقیت به دیجی‌کالا متصل شد.");
        } else {
          const payload = {
            product_id: this.form.product_id,
            product_sku_id: this.form.product_sku_id || null,
            marketplace: this.marketplace,
            external_id: String(this.form.external_id),
            external_variant_id: this.form.external_variant_id ? String(this.form.external_variant_id) : null,
            active: this.form.active,
          };
          await this.$api.post("/v1/marketplace-listings/store", payload);
          this.$toast.success("اتصال به مارکت‌پلیس ایجاد شد.");
        }

        this.$emit("saved");
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "خطا در برقراری اتصال یا ذخیره اطلاعات");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
